import React, { useEffect, forwardRef, Component, RefObject, createRef, ClassAttributes, useRef } from 'react';
import { CustomObject } from '../../../types/data';
import { useSpacePhotoTexture } from '../../../hooks';
import { BackSide, Material, MathUtils, Mesh, MeshBasicMaterial, Texture } from 'three';
import { ReactThreeFiber } from 'react-three-fiber';
import { isArray, isFunction } from 'lodash';
import anime, { AnimeInstance } from 'animejs';

const PhotoSpaceContent = forwardRef<
  Mesh,
  {
    data: CustomObject;
    radius: number;
    renderOrder?: number;
    rotation?: ReactThreeFiber.Euler;
    onLoad: () => void;
  }
>(({ data, radius, onLoad, ...props }, ref) => {
  const { loaded, texture } = useSpacePhotoTexture({
    type: data.video_thumbnail_url ? 'pano' : 'cube',
    source: data.video_thumbnail_url ? data.video_thumbnail_url : data.urls,
  });
  const matRef = useRef<Material>();

  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.opacity = 0;
  }, [data.id]);

  useEffect(() => {
    if (loaded && isFunction(onLoad)) onLoad();
  }, [loaded, onLoad]);

  return (
    <mesh ref={ref} {...props}>
      <sphereGeometry attach="geometry" args={[radius, 32, 32]} />
      <meshBasicMaterial attach="material" side={BackSide} transparent ref={matRef}>
        <primitive attach="envMap" object={texture} />
      </meshBasicMaterial>
    </mesh>
  );
});

const PhotoCubeSpaceContent = forwardRef<
  Mesh,
  {
    data: CustomObject;
    radius: number;
    renderOrder?: number;
    rotation?: ReactThreeFiber.Euler;
    onLoad: () => void;
  }
>(({ data, radius, onLoad, ...props }, ref) => {
  const { loaded, texture } = useSpacePhotoTexture({
    type: 'multi',
    source: data.urls,
  });

  useEffect(() => {
    if (loaded && isFunction(onLoad)) onLoad();
  }, [loaded, onLoad]);

  return (
    <group scale={[-1, 1, 1]}>
      <mesh
        ref={ref}
        {...props}
        material={(texture as Texture[]).map((txt, i) => {
          if (i === 2 || i === 3) {
            txt.center.set(0.5, 0.5);
            txt.rotation = MathUtils.degToRad(180);
          }

          const mat = new MeshBasicMaterial({
            map: txt,
            side: BackSide,
            transparent: true,
            opacity: 0,
          });
          return mat;
        })}>
        <boxGeometry args={[radius, radius, radius]} />
      </mesh>
    </group>
  );
});

type TPhotoSpaceProps = ClassAttributes<{}> & {
  data: CustomObject;
  radius: number;
  rotation?: ReactThreeFiber.Euler;
  renderOrder?: number;
  enableAnimation?: boolean;
  resourceId?: string;
  type?: 'cube' | 'env';
  onLoad?: () => void;
};

type TPhotoSpaceState = {
  contentData?: {
    id: number;
    urls: string[] | undefined;
    video_thumbnail_url: string | undefined;
  };
};

class PhotoSpace extends Component<TPhotoSpaceProps, TPhotoSpaceState> {
  private animeInstance: AnimeInstance;
  private meshRef: RefObject<Mesh>;
  private animParams: {
    opacity: number;
  };

  static defaultProps = {
    type: 'env',
  };

  constructor(props: Readonly<TPhotoSpaceProps>) {
    super(props);

    this.state = {
      contentData: undefined,
    };

    this.meshRef = createRef();

    this.animParams = {
      opacity: 0,
    };

    this.animeInstance = this.createAnimInstance();
  }

  componentDidMount() {
    this.updateContentData();
  }

  componentDidUpdate() {
    this.updateContentData();
  }

  render() {
    const { radius, renderOrder, rotation, type } = this.props;
    const { contentData } = this.state;

    const Component = type === 'cube' ? PhotoCubeSpaceContent : PhotoSpaceContent;

    if (contentData)
      return (
        <Component
          data={contentData}
          radius={radius}
          renderOrder={renderOrder}
          rotation={rotation}
          onLoad={this.handleTextureLoad}
          ref={this.meshRef}
        />
      );

    return null;
  }

  private handleTextureLoad = () => {
    if (this.animeInstance.began && !this.animeInstance.completed) return;
    this.animReset();
    this.animPlay();
  };

  private updateContentData = () => {
    const { data, type } = this.props;

    if (data?.box && data.id !== this.state.contentData?.id) {
      this.setState(st => ({
        ...st,
        contentData: {
          id: data.id,
          urls: data.box
            ? type === 'cube'
              ? [data.box['l'], data.box['r'], data.box['u'], data.box['d'], data.box['b'], data.box['f']]
              : [data.box['r'], data.box['l'], data.box['d'], data.box['u'], data.box['f'], data.box['b']]
            : undefined,
          video_thumbnail_url: data.video_thumbnail_url,
        },
      }));
    }
  };

  private createAnimInstance = () => {
    const duration = 1000;

    this.animParams = {
      opacity: 0,
    };

    const animeInstance = anime({
      targets: this.animParams,
      /// animated props
      opacity: 1,
      /// end animated props
      loop: false,
      autoplay: false,
      duration,
      easing: 'linear',
      update: this.animUpdate,
      complete: this.animComplete,
    });

    return animeInstance;
  };

  private animReset = () => {
    this.animeInstance?.pause();
    this.animeInstance = this.createAnimInstance();
  };

  private animPlay = () => {
    if (this.props.enableAnimation) {
      this.animeInstance.play();
    } else {
      this.updateMaterial(1);
      this.animComplete();
    }
  };

  private updateMaterial(opacity: number) {
    if (!this.meshRef.current) return;

    const { material } = this.meshRef.current;

    if (isArray(material)) {
      material.forEach(mt => (mt.opacity = opacity));
    } else {
      material.opacity = opacity;
    }
  }

  /// animation callbacks
  private animUpdate = () => {
    const { opacity } = this.animParams;
    this.updateMaterial(opacity);
  };

  private animComplete = () => {
    const { onLoad } = this.props;

    if (isFunction(onLoad)) {
      onLoad();
    }
  };
}

export default PhotoSpace;
